import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useEffect, useState, useContext, useCallback, useRef } from "react";

import LoadingLogo from "@/components/loading-logo/loading-logo";

import { testpageService } from "@/services/TestPageService";
import { authorizationService } from "@/services/continente-credentials/common/AuthorizationService";

import { Config } from "@/config/Config";
import { tryCatch } from "@/utils/try-catch";
import { useTheme } from "@/providers/theme-provider";
import useGetDigitalAssetId from "@/hooks/useGetDigitalAssetId";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import SendAuthorizationCodeMessage from "@/utils/send-message/send-message";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { ValidatePasswordResponse } from "@/models/continente-credentials/mobile/ValidatePasswordResponse";
import { Check, CheckCircle, Loader, LoaderCircle, X } from "lucide-react";

type tokenBadgeStatusOptions = "loading" | "success" | "fail"

export default function AuthSuccessPage({ digitalAssetId }: { digitalAssetId: string }) {
    const { t } = useTranslation();
    const [authorizationCode, setAuthorizationCode] = useState("");
    const [codeVerifier, setCodeVerifier] = useState("");
    const [deeplinkAction, setDeeplinkAction] = useState("");
    const { handleResponse } = useApiResponseHandler();
    const [tokenBadgeStatus, setTokenBadgeStatus] = useState<tokenBadgeStatusOptions>("loading")

    const digitalAssetContext = useContext(DigitalAssetContext);
    const location = useLocation<ValidatePasswordResponse>();
    const { theme } = useTheme();

    const hasAuthorizedRef = useRef(false);

    const handleAuthorize = useCallback(async () => {
        const { data: response, error } = await tryCatch(authorizationService.authorize({
            clientId: digitalAssetId || "",
        }));

        if (error || !response) {
            setTokenBadgeStatus("fail");
            return;
        }

        handleResponse(response, {
            onSuccess: (response) => {
                setAuthorizationCode(response.value.authorizationCode);
                setCodeVerifier(response.value.codeVerifier);

                document.getElementById("success__label")?.focus();

                setTokenBadgeStatus("success");

                SendAuthorizationCodeMessage({
                    type: "success",
                    authorization_code: response.value.authorizationCode,
                    code_verifier: response.value.codeVerifier,
                });
            },
        }, error);
    }, [digitalAssetId, handleResponse])

    useEffect(() => {
        if (hasAuthorizedRef.current) return;
        hasAuthorizedRef.current = true;

        const returnUrl = location.state?.properties?.returnUrl;

        if (returnUrl) {
            window.location.href = returnUrl;
        } else {
            handleAuthorize();
        }
    }, [handleAuthorize, location.state]);

    const handleOpenSiteCredentialsClick = async () => {
        if (!digitalAssetContext.digitalAsset.showSiteCredentialsLink) return;

        if (tokenBadgeStatus == "fail") return;

        if (authorizationCode === "" || codeVerifier === "") {
            alert(
                "Authorization Code ou Code Verifier não estavam prontos. Tente Novamente"
            );
        } else {

            const { data: response, error } = await tryCatch(testpageService.getSiteCredentialsUrl(
                authorizationCode,
                codeVerifier,
                digitalAssetId || ""
            ));

            if (error || !response) {
                return;
            }

            handleResponse(response, {
                onSuccess: (response) => {
                    const clientId = digitalAssetId ?? "";
                    const token = response.value?.siteCredenciaisToken ?? "";
                    window.location.href = `/site-credentials/authorize?clientId=${clientId}&token=${token}&theme=${theme}&deeplinkAction=${deeplinkAction}`;
                }
            }, error);
        }
    };

    function SiteCredentialsTokenBadge() {
        if (tokenBadgeStatus == "success") {
            return <CheckCircle className="bg-[#27a255] p-2 rounded-full text-white shadow-xl" />
        }

        if (tokenBadgeStatus == "fail") {
            return <X className="bg-red-500 p-2 rounded-full text-white shadow-xl" />
        }

        return <LoaderCircle className="animate-spin" />
    }

    return (
        <div className="w-full flex flex-col items-center justify-center min-h-[300px] py-12">
            {digitalAssetContext.digitalAsset.showSiteCredentialsLink && (
                <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[10000] flex flex-col items-center gap-12">
                    <select name="category" value={deeplinkAction} onChange={event => setDeeplinkAction(event.target.value)}>
                        <option value={""}>NULL</option>
                        <option value={Config.DeepLinkActions.sc_home}>Home</option>
                        <option value={Config.DeepLinkActions.sc_email_definition}>email_definition</option>
                        <option value={Config.DeepLinkActions.sc_email_update}>email_update</option>
                        <option value={Config.DeepLinkActions.sc_phoneNumber_definition}>phoneNumber_definition</option>
                        <option value={Config.DeepLinkActions.sc_phoneNumber_update}>phoneNumber_update</option>
                        <option value={Config.DeepLinkActions.sc_password_definition}>password_definition</option>
                        <option value={Config.DeepLinkActions.sc_password_update}>password_update</option>
                        <option value={Config.DeepLinkActions.sc_loyaltyCard_definition}>loyaltyCard_definition</option>
                        <option value={Config.DeepLinkActions.sc_faqs}>faqs</option>
                        <option value={Config.DeepLinkActions.sc_consents}>consents</option>
                    </select>
                    <span
                        onClick={() => { handleOpenSiteCredentialsClick() }}
                        className="text-[var(--on-surface-variant)] hover:underline cursor-pointer font-bold"
                        style={{
                            zIndex: 10000,
                            position: "absolute",
                            marginTop: "100px",
                        }}
                    >
                        <div className="flex gap-4">
                            Abrir Site Credenciais
                            <SiteCredentialsTokenBadge />
                        </div>
                    </span>
                </div>
            )
            }
            <div
                id="success__label"
                tabIndex={10}
                aria-label={t("accessibility.deadend.success_message")}
                aria-live="assertive"
                role="alert">
            </div>
            <LoadingLogo />
        </div >
    );
}


export const AuthSuccessQueryPage = () => {
    const digitalAssetId = useGetDigitalAssetId() || "1";
    return <AuthSuccessPage digitalAssetId={digitalAssetId} />;
};


export const AuthSuccessRoutePage = () => {
    let params = useParams<{ clientId: string }>();
    const digitalAssetId = params.clientId;

    return <AuthSuccessPage digitalAssetId={digitalAssetId} />;
};